import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Modal, Text } from '@fragments';
import { IconFileUpload, IconInfoCircle, IconReplace } from '@tabler/icons-react';
import ArtPreview from '../view/components/ArtPreview';
import { readTemplateFile, TEMPLATE_FILE_MAX_BYTES } from './templateFile';

const FILE_ERRORS = { json: 'importErrorJson', shape: 'importErrorShape', format: 'importErrorFormat', invalid: 'importErrorInvalid', size: 'importErrorSize' };
const FORMAT_KEYS = { 'instagram::post': 'formatPost', 'instagram::carousel': 'formatCarousel' };
// A format's name: the host's label when it lists the format, else the editor's own.
export const formatNameOf = (t, id, formats = []) => formats.find(item => item.id === id)?.label || (FORMAT_KEYS[id] ? t(FORMAT_KEYS[id]) : id);
const COPY = {
  import: { title: 'importTitle', subtitle: 'importSubtitle', submit: 'importSubmit' },
  save: { title: 'saveTemplateTitle', subtitle: 'saveTemplateSubtitle', submit: 'saveTemplateSubmit' },
  edit: { title: 'editTemplateTitle', subtitle: null, submit: 'editTemplateSubmit' },
  export: { title: 'exportTemplateTitle', subtitle: 'exportTemplateSubtitle', submit: 'exportTemplateSubmit' },
};
// "lista, dicas , list" → ['lista', 'dicas', 'list'] (the limits `readTemplateFile` keeps).
const tagsOf = text => [...new Set(text.split(',').map(tag => tag.trim().slice(0, 40)).filter(Boolean))].slice(0, 10);

// A personal template's details: its name, category and description, with its first page.
// `mode` is where it comes from:
// - `import` ("Adicionar modelo"): the creator drops (or picks) a template's JSON file first;
// - `save` ("Salvar como modelo"): `document` is the art on the stage;
// - `edit` (a personal template's menu): `initial` holds its details, `document` its pages
//   once loaded;
// - `export` (the template generator's "Exportar JSON"): like `save`, with its tags too.
// `onSubmit({ name, description, category_key, tags, document })` stores it and answers the
// template; a rejection keeps the dialog open with the reason (`code: 'image_copy'` with a
// `count` when an imported file's images could not be copied, `validation_error` with paths).
// `notice` is a line from the host under the file (what the export carries).
export default function TemplateDialog({ open, mode = 'import', onClose, format, formatName, categories, category = null, document: given = null, initial = null, notice = null, onSubmit }) {
  const { t, i18n } = useTranslation('artEditor');
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [problem, setProblem] = useState(null);
  const [name, setName] = useState('');
  const [categoryKey, setCategoryKey] = useState(null);
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [over, setOver] = useState(false);
  const [saving, setSaving] = useState(false);
  const known = key => categories.some(item => item.key === key);
  const pickCategory = key => (known(key) ? key : known(category) ? category : categories[0]?.key || null);
  useEffect(() => {
    setFile(null); setProblem(null); setOver(false); setSaving(false);
    if (!open) return;
    setName(initial?.name || '');
    setDescription(initial?.description || '');
    setTags((initial?.tags || []).join(', '));
    setCategoryKey(pickCategory(initial?.category_key));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  // Categories may arrive after the dialog opens (the editor loads them on demand).
  useEffect(() => { if (open && !categoryKey && categories.length) setCategoryKey(pickCategory(initial?.category_key)); }, [categories]); // eslint-disable-line react-hooks/exhaustive-deps

  const read = async picked => {
    if (!picked) return;
    const result = picked.size > TEMPLATE_FILE_MAX_BYTES ? { error: 'size' }
      : readTemplateFile(await picked.text(), { format, fileName: picked.name, language: i18n.language });
    if (result.error) { setFile(null); setProblem(result); return; }
    setProblem(null);
    setFile({ name: picked.name, template: result.template });
    setName(result.template.name);
    setDescription(result.template.description || '');
    setCategoryKey(pickCategory(result.template.category_key));
  };
  const doc = mode === 'import' ? file?.template.document : given;
  const ready = (mode !== 'import' || !!file) && !!name.trim() && !!categoryKey;
  const submit = async () => {
    if (!ready || saving) return;
    setSaving(true);
    setProblem(null);
    try {
      await onSubmit({ tags: initial?.tags || [], ...(mode === 'import' ? file.template : {}), ...(mode === 'save' || mode === 'export' ? { document: given } : {}),
        ...(mode === 'export' ? { tags: tagsOf(tags) } : {}), name: name.trim(), description: description.trim() || null, category_key: categoryKey });
    } catch (failure) {
      const paths = Object.keys(failure?.errors || {});
      setProblem(failure?.code === 'image_copy' ? { error: 'images', count: failure.count || 1 }
        : failure?.code === 'validation_error' && paths.length ? { error: 'rejected', paths: paths.slice(0, 3) } : { error: 'save' });
      setSaving(false);
    }
  };

  const message = !problem ? null : problem.error === 'rejected' ? t('templateErrorRejected', { paths: problem.paths.join(', ') }) : problem.error === 'save' ? t('templateErrorSave')
    : problem.error === 'images' ? t('importErrorImages', { count: problem.count })
      : t(FILE_ERRORS[problem.error], { format: formatName(problem.format), current: formatName(format), paths: (problem.paths || []).join(', '), size: TEMPLATE_FILE_MAX_BYTES / 1024 / 1024 });
  const copy = COPY[mode];
  const ratio = doc ? `${doc.width} / ${doc.height}` : '4 / 5';
  return <Modal open={open} onClose={onClose} size="md" className="art-import-modal" closeLabel={t('close')} title={t(copy.title)}
    subtitle={copy.subtitle ? t(copy.subtitle, { format: formatName(format) }) : undefined}
    footer={<>
      <Button variant="rounded" color="overlay" preventLoading onClick={onClose}>{t('cancel')}</Button>
      <Button variant="rounded" color="inverse" disabled={!ready} loading={saving} onClick={submit}>{t(copy.submit)}</Button>
    </>}>
    <form className="art-import" onSubmit={event => { event.preventDefault(); submit(); }}>
      {mode === 'import' && <input ref={input} type="file" accept=".json,application/json" hidden onChange={event => { const [picked] = event.target.files; event.target.value = ''; read(picked); }} />}
      {mode === 'import' && !doc
        ? <div className={`art-import__drop ${over ? 'is-over' : ''}`}
          onDragOver={event => { event.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
          onDrop={event => { event.preventDefault(); setOver(false); read(event.dataTransfer.files?.[0]); }}>
          <IconFileUpload aria-hidden="true" />
          <Text.label contrast={9}>{t('importDrop')}</Text.label>
          <Text.caption contrast={6}>{t('importDropHint', { format: formatName(format) })}</Text.caption>
          <Button type="button" size="small" variant="rounded" color="overlay" preventLoading onClick={() => input.current?.click()}>{t('importChoose')}</Button>
        </div>
        : <div className="art-import__file">
          <span className="art-import__thumb" style={{ aspectRatio: ratio }}>{doc ? <ArtPreview document={doc} rounded={false} /> : <span className="art-import__thumb-empty art-skeleton" />}</span>
          <span className="art-import__file-text">
            <Text.label contrast={9} className="art-import__file-name">{mode === 'import' ? file.name : name.trim() || initial?.name || t('templateUntitled')}</Text.label>
            {doc && <Text.caption contrast={6}>{`${formatName(doc.format)} · ${t('pageCount', { count: doc.pages.length })}`}</Text.caption>}
          </span>
          {mode === 'import' && <Button type="button" size="small" variant="rounded" color="overlay" preventLoading startIcon={<IconReplace />} onClick={() => input.current?.click()}>{t('importReplace')}</Button>}
        </div>}
      {notice && <div className="art-import__notice" role="note"><Text.caption contrast={7} left={<IconInfoCircle />}>{notice}</Text.caption></div>}
      {message && <div className="art-inline-error" role="alert"><Text.caption contrast={8}>{message}</Text.caption></div>}
      {doc || mode !== 'import' ? <>
        <Input value={name} onInput={setName} label={t('importName')} aria-label={t('importName')} placeholder={t('importNamePlaceholder')} maxLength={80} />
        <div className="art-import__group">
          <Text.label contrast={9}>{t('importCategory')}</Text.label>
          <div className="art-import__chips" role="radiogroup" aria-label={t('importCategory')}>
            {categories.map(item => <Button key={item.key} type="button" role="radio" aria-checked={categoryKey === item.key} size="small" variant="rounded" color={categoryKey === item.key ? 'inverse' : 'overlay'}
              preventLoading onClick={() => setCategoryKey(item.key)}>{item.name}</Button>)}
          </div>
        </div>
        <Input value={description} onInput={setDescription} label={t('importDescription')} aria-label={t('importDescription')} placeholder={t('importDescriptionPlaceholder')} maxLength={280} />
        {mode === 'export' && <Input value={tags} onInput={setTags} label={t('importTags')} aria-label={t('importTags')} placeholder={t('importTagsPlaceholder')} maxLength={440} />}
      </> : null}
    </form>
  </Modal>;
}
